import { useEffect, useRef } from 'react';

import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect';

type KeyboardEventHandler = (evt: KeyboardEvent) => void;

type IProps = Partial<{
    target?: GlobalEventHandlers | null;
    onEsc: KeyboardEventHandler;
    onDown: KeyboardEventHandler;
    onUp: KeyboardEventHandler;
    onLeft: KeyboardEventHandler;
    onRight: KeyboardEventHandler;
    onTab: KeyboardEventHandler;
    onHome: KeyboardEventHandler;
    onEnd: KeyboardEventHandler;
    onPageDown: KeyboardEventHandler;
    onPageUp: KeyboardEventHandler;
    onSpace: KeyboardEventHandler;
}>;

export const useKeyboard = ({
    onEsc,
    onUp,
    onDown,
    onLeft,
    onRight,
    onTab,
    onHome,
    onEnd,
    onPageDown,
    onPageUp,
    onSpace,
    target = typeof window === 'undefined' ? undefined : window,
}: IProps) => {
    const keyCodeToCallbackRef = useRef<Record<string, KeyboardEventHandler | undefined>>({});

    useIsomorphicLayoutEffect(() => {
        keyCodeToCallbackRef.current = {
            ArrowDown: onDown,
            Escape: onEsc,
            Tab: onTab,
            ArrowUp: onUp,
            ArrowLeft: onLeft,
            ArrowRight: onRight,
            Home: onHome,
            End: onEnd,
            PageDown: onPageDown,
            PageUp: onPageUp,
            Space: onSpace,
        };
    }, [onDown, onEsc, onTab, onUp, onLeft, onRight, onHome, onEnd, onPageDown, onPageUp, onSpace]);

    useEffect(() => {
        const keydownHandler = (event: KeyboardEvent) =>
            keyCodeToCallbackRef.current[event.code]?.(event);

        if (!target) {
            return undefined;
        }

        target.addEventListener('keydown', keydownHandler);

        return () => {
            target.removeEventListener('keydown', keydownHandler);
        };
    }, [target]);
};
